import { css, cx } from "@emotion/css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow, { TableRowProps } from "@mui/material/TableRow";
import { type SortDirection, Table as TSTable, flexRender } from "@tanstack/react-table";
import { TableVirtuoso } from "react-virtuoso";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import type { AriaProps, DataProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

type MuiTableProps<T> = {
  id?: string;
  table: TSTable<T>;
  handleRowClick?: (row: T) => void;
  className?: string;
  getRowAttributes?: (row: T) => TableRowProps<"tr", DataProps & AriaProps>;
  classes?: StyleClasses<"container" | "table" | "header" | "headerRow" | "headerCell" | "body" | "row" | "cell">;
};

export const MuiTable = <T,>({ id, table, handleRowClick, className, getRowAttributes, classes }: MuiTableProps<T>) => {
  const styles = useStyles(makeStyles);
  // Only show the sort affordance on tables that opted into sorting (i.e. provided a sorted
  // row model). Without this guard, accessor columns on non-sorting tables report
  // `getCanSort() === true` and would render dead sort arrows.
  const sortingEnabled = table.options.getSortedRowModel != null;

  return (
    <TableContainer id={id} className={cx(styles.container, className, classes?.container)}>
      <TableVirtuoso
        components={{
          Table: props => <Table {...props} size="medium" className={cx(styles.table, classes?.table)} />,
          TableHead: props => <TableHead {...props} className={cx(styles.header, classes?.header)} />,
          TableBody: props => <TableBody {...props} className={cx(styles.body, classes?.body)} />,
          TableRow: props => {
            return (
              <TableRow
                {...props}
                id={suffixId(id, `row-${props.item.id}`)}
                {...getRowAttributes?.(props.item.original)}
                hover
                selected={props.item.getIsSelected()}
                onMouseEnter={() => props.item.setHovered?.()}
                onMouseLeave={() => props.item.setNotHovered?.()}
                className={cx({ [styles.row]: true, [styles.selected]: props.item.getIsSelected() }, classes?.row)}
                onClick={() => {
                  if (props.item.getCanSelect() && !handleRowClick) {
                    props.item.toggleSelected();
                  }
                  if (handleRowClick) {
                    handleRowClick(props.item.original);
                  }
                }}
              />
            );
          },
        }}
        data={table.getRowModel().rows}
        useWindowScroll
        totalCount={100}
        fixedHeaderContent={() =>
          table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id} className={cx(styles.headerRow, classes?.headerRow)}>
              {headerGroup.headers.map(header => {
                const canSort = sortingEnabled && header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();
                const toggleSort = () => {
                  table.setSorting(old => {
                    const currentColumnSort = old.find(value => value.id === header.column.id);
                    if (!currentColumnSort) {
                      return [
                        {
                          id: header.column.id,
                          desc: false, // ASC
                        },
                      ];
                    }
                    if (!currentColumnSort.desc) {
                      // Id ASC
                      return [
                        {
                          id: header.column.id,
                          desc: true, // DESC
                        },
                      ];
                    } else {
                      // Id DESC
                      return [];
                    }
                  });
                };
                return (
                  <TableCell
                    key={header.id}
                    id={suffixId(id, `header-${header.column.id}`)}
                    aria-sort={canSort ? ariaSort(sortDirection) : undefined}
                    className={cx(
                      {
                        [styles.headerCell]: true,
                        [styles.sorted]: !!sortDirection,
                      },
                      css({ width: `${header.column.getSize()}%` }),
                      classes?.headerCell,
                    )}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <Button
                        variant="text"
                        data-action={`sort-table-${id}-${header.column.id}`}
                        onClick={toggleSort}
                        className={styles.sortableHeader}
                        endIcon={
                          !sortDirection ? (
                            <Icons.chevronUpDown className={cx(styles.sortIcon, styles.sortOff)} />
                          ) : sortDirection === "asc" ? (
                            <Icons.chevronUp className={styles.sortIcon} />
                          ) : (
                            <Icons.chevronDown className={styles.sortIcon} />
                          )
                        }
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        }
        itemContent={(_, row) =>
          row.getVisibleCells().map(cell => {
            return (
              <TableCell
                key={cell.id}
                className={cx(
                  styles.cell,
                  css({
                    width: `${cell.column.getSize()}%`,
                  }),
                  classes?.cell,
                )}
              >
                {flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  meta: {},
                })}
              </TableCell>
            );
          })
        }
      />
    </TableContainer>
  );
};

const ariaSort = (direction: false | SortDirection): "ascending" | "descending" | "none" =>
  direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none";

const makeStyles = (theme: Theme) => ({
  container: css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
  }),
  table: css({
    width: "100%",
    tableLayout: "fixed",
  }),
  header: css({
    "& > *": {
      borderBottom: "none",
    },
    padding: 0,
    margin: 0,
  }),
  headerRow: css({
    width: "100%",
    overflow: "clip",
    "& > *": {
      borderBottom: "none",
    },
  }),
  headerCell: css({
    display: "table-cell",
    "& > *": {
      borderBottom: "none",
    },
    padding: 0,
    margin: 0,
  }),
  sortableHeader: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "stretch",
    gap: theme.spacing(4),
    width: "100%",
    "& > :first-child": {
      textWrap: "nowrap",
      flex: "1 1 0",
      display: "inline-block",
      textOverflow: "ellipsis",
      minWidth: 0,
      overflow: "clip",
    },
  }),
  body: css({}),
  row: css({
    width: "100%",
    overflow: "clip",
    "& > *": {
      borderBottom: "none",
    },
    cursor: "pointer",
  }),
  cell: css({
    padding: 0,
    minWidth: 0,
    overflow: "clip",
  }),
  selected: css({
    backgroundColor: `rgb(from ${theme.palette.primary.light} r g b / 0.2)`,
  }),
  sortIcon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
  sortOff: css({
    color: theme.palette.grey[300],
  }),
  sorted: css({
    "& *": {
      fontWeight: "bold",
    },
  }),
});
